import { FC, useState } from "react";
import Input from "../../../shared/YasuLib/Input/Input";
import useFirebase from "../../../../context/FirebaseProvider/useFirebase";
import { useForm } from "react-hook-form";
import { httpsCallable } from "firebase/functions";
import { Alert, Grid, Snackbar } from "@mui/material";
import { GenericProps } from "../../../../types/types";
import { emailValidator } from "../../../../utils/formValidators";
import PillButton from "../../../shared/YasuLib/PillButton/PillButton";
import Textarea from "../../../shared/YasuLib/Textarea/Textarea";

interface ContactUsFormProps {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}

const ContactUsForm: FC<GenericProps> = ({ className = "" }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<ContactUsFormProps>();
  const { functions } = useFirebase();

  // closes the success snackbar
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  // form submission
  const onSubmit = async (data: ContactUsFormProps) => {
    // start loading
    setIsLoading(true);
    try {
      // call cloud function
      const sendContactEmail = httpsCallable<ContactUsFormProps>(functions, "sendContactEmail");
      await sendContactEmail(data);

      // cleanup -> open success snackbar and reset form
      setOpen(true);
      reset();
    } catch (err) {
      console.error("Error sending contact us email ", err);
      // default generic error message
      let errorMessage = "Error sending contact us email";

      // if proper error get error message
      if (err instanceof Error) {
        errorMessage = err.message;
      }

      // set custom form error
      setError("email", {
        type: "manual",
        message: errorMessage,
      });
    } finally {
      // stop loading
      setIsLoading(false);
    }
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={`ContactUsForm ${className}`} noValidate>
        <Grid container spacing={0.5} columnSpacing={{ xs: 0.5, sm: 1 }}>
          <Grid item xs={6} sm={6}>
            <Input
              type="text"
              {...register("firstName", { required: "First name is required" })}
              errorMessage={errors.firstName?.message}
              placeholder="First Name"
              autoComplete="off" // covers error message
            />
          </Grid>
          <Grid item xs={6} sm={6}>
            <Input
              type="text"
              {...register("lastName", { required: "Last name is required" })}
              errorMessage={errors.lastName?.message}
              placeholder="Last Name"
              autoComplete="off" // covers error message
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              type="email"
              {...register("email", emailValidator)}
              errorMessage={errors.email?.message}
              placeholder="Email"
              autoComplete="off" // covers error message
            />
          </Grid>
          <Grid item xs={12}>
            <Textarea
              {...register("message", { required: "Message is required" })}
              errorMessage={errors.message?.message}
              placeholder="Type your message here"
              rows={5}
              autoComplete="off" // covers error message
            />
          </Grid>
        </Grid>

        <PillButton type="submit" isLoading={isLoading} disabled={isLoading}>
          Submit
        </PillButton>
      </form>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} className="text-white" variant="filled" severity="success">
          Success! Thanks for reaching out
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactUsForm;
